import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const PersonalityQuestions = ({ onComplete, onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/questions`);
        setQuestions(response.data.questions);
      } catch (error) {
        console.error("Failed to fetch questions:", error);
        toast.error("Failed to load questions. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const handleAnswerSelect = (answerKey) => {
    if (isAdvancing) return;

    const currentQuestion = questions[currentIndex];
    setSelectedAnswer(answerKey);
    setIsAdvancing(true);

    const newAnswer = {
      question_id: currentQuestion.id,
      category: currentQuestion.category,
      category_label: currentQuestion.category_label,
      question_text: currentQuestion.text,
      answer: answerKey,
      answer_text: currentQuestion.options[answerKey]
    };

    const updatedAnswers = [...answers, newAnswer];

    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setAnswers(updatedAnswers);
        setCurrentIndex((prev) => prev + 1);
        setSelectedAnswer(null);
        setIsAdvancing(false);
      } else {
        onComplete(updatedAnswers);
      }
    }, 300);
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen flex flex-col items-center justify-center px-6"
        data-testid="questions-loading"
      >
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-neon-purple animate-spin mx-auto mb-4" />
          <p className="font-orbitron text-white/60 tracking-widest text-sm uppercase">
            Loading Assessment...
          </p>
        </div>
      </motion.div>
    );
  }

  if (!questions.length) {
    return null;
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex) / questions.length) * 100;
  const answerKeys = ["A", "B", "C", "D"];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6 py-12 overflow-hidden"
      data-testid="personality-questions"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-neon-purple/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-neon-cyan/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors font-rajdhani text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <span className="font-orbitron text-white/40 text-xs tracking-widest uppercase">
            Question {currentIndex + 1} of {questions.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-neon-purple to-neon-cyan shadow-[0_0_10px_rgba(127,90,240,0.5)]"
            />
          </div>
        </div>

        {/* Question card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Category label */}
            <div className="mb-4">
              <span className="font-orbitron text-xs tracking-widest text-neon-cyan/70 uppercase">
                {currentQuestion.category_label}
              </span>
            </div>

            {/* Question text */}
            <h2 className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-8 leading-relaxed">
              {currentQuestion.text}
            </h2>

            {/* Answer buttons */}
            <div className="space-y-3">
              {answerKeys.map((key) => {
                const isSelected = selectedAnswer === key;
                return (
                  <motion.button
                    key={key}
                    onClick={() => handleAnswerSelect(key)}
                    disabled={isAdvancing}
                    whileHover={!isAdvancing ? { scale: 1.01 } : {}}
                    whileTap={!isAdvancing ? { scale: 0.99 } : {}}
                    className={`
                      w-full text-left p-4 rounded-sm border transition-all duration-200
                      font-rajdhani text-base
                      flex items-start gap-4
                      ${isSelected
                        ? "border-neon-purple bg-neon-purple/20 text-white shadow-[0_0_20px_rgba(127,90,240,0.3)]"
                        : "border-white/10 bg-white/5 text-white/70 hover:border-neon-purple/50 hover:bg-white/8 hover:text-white"
                      }
                      disabled:cursor-not-allowed
                    `}
                  >
                    {/* Answer key badge */}
                    <span
                      className={`
                        flex-shrink-0 w-7 h-7 rounded-sm border flex items-center justify-center
                        font-orbitron text-xs font-bold transition-colors duration-200
                        ${isSelected
                          ? "border-neon-purple bg-neon-purple text-white"
                          : "border-white/20 text-white/40"
                        }
                      `}
                    >
                      {key}
                    </span>
                    <span className="leading-relaxed pt-0.5">
                      {currentQuestion.options[key]}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dots indicator */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i < currentIndex
                  ? "w-2 h-2 bg-neon-purple"
                  : i === currentIndex
                  ? "w-3 h-3 bg-neon-cyan shadow-[0_0_8px_rgba(0,212,255,0.6)]"
                  : "w-2 h-2 bg-white/15"
              }`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default PersonalityQuestions;
